import React, { useState } from 'react';
import { Download, Upload, ArrowLeftRight, PieChart, ExternalLink, Lock, Unlock, Plus, Minus, TrendingUp, TrendingDown, X } from 'lucide-react';
import { assetHoldings, futuresPositions } from '../../utils/portfolioData';
interface BalancesSectionProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
// Mock staking data - Updated with correct annual rewards calculations
const stakingAssets = [{
  symbol: 'ETH',
  name: 'Ethereum',
  logo: 'https://cryptologos.cc/logos/ethereum-eth-logo.svg',
  platform: 'Kraken',
  stakedAmount: 3.802,
  currentPrice: 3287.0,
  apy: 4.5,
  lockupPeriod: 'Flexible',
  estimatedRewards: 0.171,
  rewardsFrequency: 'Weekly',
  totalValue: 12497.37
}, {
  symbol: 'SOL',
  name: 'Solana',
  logo: 'https://cryptologos.cc/logos/solana-sol-logo.svg',
  platform: 'Kraken',
  stakedAmount: 21.23,
  currentPrice: 141.33,
  apy: 6.8,
  lockupPeriod: 'Flexible',
  estimatedRewards: 1.44,
  rewardsFrequency: 'Weekly',
  totalValue: 3000.23
}, {
  symbol: 'DOT',
  name: 'Polkadot',
  logo: 'https://cryptologos.cc/logos/polkadot-new-dot-logo.svg',
  platform: 'Kraken',
  stakedAmount: 150.0,
  currentPrice: 6.82,
  apy: 12.5,
  lockupPeriod: '28 days',
  estimatedRewards: 18.75,
  rewardsFrequency: 'Daily',
  totalValue: 1023.0
}, {
  symbol: 'ADA',
  name: 'Cardano',
  logo: 'https://cryptologos.cc/logos/cardano-ada-logo.svg',
  platform: 'Kraken',
  stakedAmount: 5000.0,
  currentPrice: 0.4523,
  apy: 5.2,
  lockupPeriod: 'Flexible',
  estimatedRewards: 260.0,
  rewardsFrequency: 'Weekly',
  totalValue: 2261.5
}];
const usdtYieldProducts = [{
  symbol: 'USDT',
  name: 'Tether',
  logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg',
  product: 'Flexible Savings',
  stakedAmount: 5000.0,
  currentPrice: 0.9995,
  apy: 3.5,
  lockupPeriod: 'Flexible',
  estimatedRewards: 175.0,
  rewardsFrequency: 'Daily',
  totalValue: 4997.5
}, {
  symbol: 'USDT',
  name: 'Tether',
  logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg',
  product: '30-Day Locked',
  stakedAmount: 10000.0,
  currentPrice: 0.9995,
  apy: 5.8,
  lockupPeriod: '30 days',
  estimatedRewards: 580.0,
  rewardsFrequency: 'Daily',
  totalValue: 9995.0
}, {
  symbol: 'USDT',
  name: 'Tether',
  logo: 'https://cryptologos.cc/logos/tether-usdt-logo.svg',
  product: '90-Day Locked',
  stakedAmount: 15000.0,
  currentPrice: 0.9995,
  apy: 7.2,
  lockupPeriod: '90 days',
  estimatedRewards: 1080.0,
  rewardsFrequency: 'Daily',
  totalValue: 14992.5
}];
export function BalancesSection({
  accountType
}: BalancesSectionProps) {
  const [activeTab, setActiveTab] = useState<'crypto-staking' | 'usdt-yield'>('crypto-staking');
  // Show portfolio holdings for main account
  if (accountType === 'main') {
    // Calculate total portfolio value
    const totalValue = assetHoldings.reduce((sum, asset) => sum + asset.spotAmount * asset.price, 0);
    // Mock 24h changes (in reality would come from API)
    const holdingsWithChange = assetHoldings.map(asset => ({
      ...asset,
      change24h: Math.random() * 10 - 5,
      change24hValue: asset.spotAmount * asset.price * (Math.random() * 10 - 5) / 100
    }));
    return <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-1447">
        {/* Header */}
        <div className="border-b border-border-subtle" data-id="element-1448">
          <div className="flex items-center justify-between px-6 py-4" data-id="element-1449">
            <div className="text-sm font-semibold text-text-primary" data-id="element-1450">
              Portfolio Holdings
            </div>
            <div className="text-right" data-id="element-1451">
              <div className="text-xs text-text-secondary" data-id="element-1452">Total Value</div>
              <div className="text-sm font-semibold text-text-primary font-numbers" data-id="element-1453">
                $
                {totalValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="md:hidden p-4 space-y-4" data-id="element-1454">
          {holdingsWithChange.map(asset => {
          const value = asset.spotAmount * asset.price;
          const allocation = value / totalValue * 100;
          return <div key={asset.symbol} className="bg-background-elevated rounded-xl p-4 space-y-3" data-id="element-1455">
                <div className="flex justify-between items-start" data-id="element-1456">
                  <div className="flex items-center gap-2.5" data-id="element-1457">
                    <img src={asset.logo} alt={asset.name} className="w-8 h-8 object-contain" data-id="element-1458" />
                    <div data-id="element-1459">
                      <div className="font-medium text-text-primary text-sm" data-id="element-1460">
                        {asset.name}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-1461">
                        {asset.symbol}
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1462">
                    <div className="font-numbers text-sm text-text-primary font-medium" data-id="element-1463">
                      $
                      {value.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1464">
                  <div data-id="element-1465">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1466">
                      Price
                    </div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1467">
                      $
                      {asset.price.toLocaleString('en-US', {
                    minimumFractionDigits: asset.symbol === 'USDT' ? 4 : 2,
                    maximumFractionDigits: asset.symbol === 'USDT' ? 4 : 2
                  })}
                    </div>
                  </div>
                  <div data-id="element-1468">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1469">
                      Amount
                    </div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1470">
                      {asset.spotAmount.toLocaleString('en-US', {
                    minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 4,
                    maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 4
                  })}
                    </div>
                  </div>
                  <div data-id="element-1471">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1472">
                      24H Change
                    </div>
                    <div className="flex items-center gap-1" data-id="element-1473">
                      {asset.change24h >= 0 ? <TrendingUp size={12} className="text-success" data-id="element-1474" /> : <TrendingDown size={12} className="text-danger" data-id="element-1475" />}
                      <div className={`font-numbers text-sm font-medium ${asset.change24h >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1476">
                        {asset.change24h >= 0 ? '+' : ''}
                        {asset.change24h.toFixed(2)}%
                      </div>
                    </div>
                  </div>
                  <div data-id="element-1477">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1478">
                      Allocation
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1479">
                      <div className="flex-1 h-1.5 bg-background-secondary rounded-full overflow-hidden" data-id="element-1480">
                        <div className="h-full bg-brand-purple rounded-full" style={{
                      width: `${allocation}%`
                    }} data-id="element-1481" />
                      </div>
                      <div className="font-numbers text-xs text-text-primary" data-id="element-1482">
                        {allocation.toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>

        {/* Holdings Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-1483">
          <table className="w-full min-w-[600px]" data-id="element-1484">
            <thead className="bg-background-elevated/50" data-id="element-1485">
              <tr className="border-b border-border-subtle" data-id="element-1486">
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1487">
                  Asset
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1488">
                  Price
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1489">
                  Amount
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1490">
                  Value
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1491">
                  24H Change
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1492">
                  Allocation
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1493">
              {holdingsWithChange.map(asset => {
              const value = asset.spotAmount * asset.price;
              const allocation = value / totalValue * 100;
              return <tr key={asset.symbol} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1494">
                    <td className="px-4 py-3" data-id="element-1495">
                      <div className="flex items-center gap-2.5" data-id="element-1496">
                        <img src={asset.logo} alt={asset.name} className="w-6 h-6 object-contain" data-id="element-1497" />
                        <div data-id="element-1498">
                          <div className="font-medium text-text-primary text-sm" data-id="element-1499">
                            {asset.name}
                          </div>
                          <div className="text-xs text-text-secondary" data-id="element-1500">
                            {asset.symbol}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1501">
                      <div className="font-numbers text-sm text-text-primary whitespace-nowrap" data-id="element-1502">
                        $
                        {asset.price.toLocaleString('en-US', {
                      minimumFractionDigits: asset.symbol === 'USDT' ? 4 : 2,
                      maximumFractionDigits: asset.symbol === 'USDT' ? 4 : 2
                    })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1503">
                      <div className="font-numbers text-sm text-text-primary whitespace-nowrap" data-id="element-1504">
                        {asset.spotAmount.toLocaleString('en-US', {
                      minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 4,
                      maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 4
                    })}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-1505">
                          {asset.symbol}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1506">
                      <div className="font-numbers text-sm text-text-primary font-medium whitespace-nowrap" data-id="element-1507">
                        $
                        {value.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1508">
                      <div className="flex items-center justify-end gap-1" data-id="element-1509">
                        {asset.change24h >= 0 ? <TrendingUp size={12} className="text-success" data-id="element-1510" /> : <TrendingDown size={12} className="text-danger" data-id="element-1511" />}
                        <div className={`font-numbers text-sm font-medium ${asset.change24h >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1512">
                          {asset.change24h >= 0 ? '+' : ''}
                          {asset.change24h.toFixed(2)}%
                        </div>
                      </div>
                      <div className="text-xs text-text-secondary whitespace-nowrap" data-id="element-1513">
                        {asset.change24hValue >= 0 ? '+' : ''}$
                        {Math.abs(asset.change24hValue).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-1514">
                      <div className="flex items-center justify-end gap-2" data-id="element-1515">
                        <div className="w-16 h-1.5 bg-background-elevated rounded-full overflow-hidden" data-id="element-1516">
                          <div className="h-full bg-brand-purple rounded-full" style={{
                        width: `${allocation}%`
                      }} data-id="element-1517" />
                        </div>
                        <div className="font-numbers text-sm text-text-primary whitespace-nowrap min-w-[3rem] text-right" data-id="element-1518">
                          {allocation.toFixed(1)}%
                        </div>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>;
  }
  // Show open futures positions for futures account
  if (accountType === 'futures') {
    // Calculate total unrealized P&L
    const totalUnrealizedPnl = futuresPositions.reduce((sum, pos) => sum + pos.unrealizedPnl, 0);
    const totalPositionValue = futuresPositions.reduce((sum, pos) => sum + pos.size * pos.markPrice, 0);
    return <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-1519">
        {/* Header */}
        <div className="border-b border-border-subtle" data-id="element-1520">
          <div className="flex items-center justify-between px-6 py-4" data-id="element-1521">
            <div className="text-sm font-semibold text-text-primary" data-id="element-1522">
              Open Positions
            </div>
            <div className="flex items-center gap-3" data-id="element-1523">
              <div className="text-right" data-id="element-1524">
                <div className="text-xs text-text-secondary" data-id="element-1525">Total Value</div>
                <div className="text-sm font-semibold text-text-primary font-numbers" data-id="element-1526">
                  $
                  {totalPositionValue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </div>
              </div>
              <div className="text-right" data-id="element-1527">
                <div className="text-xs text-text-secondary" data-id="element-1528">
                  Unrealized P&L
                </div>
                <div className={`text-sm font-semibold font-numbers ${totalUnrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1529">
                  {totalUnrealizedPnl >= 0 ? '+' : ''}$
                  {totalUnrealizedPnl.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Cards View */}
        <div className="md:hidden p-4 space-y-4" data-id="element-1530">
          {futuresPositions.map((position, idx) => {
          const positionValue = position.size * position.markPrice;
          return <div key={`${position.pair}-${idx}`} className="bg-background-elevated rounded-xl p-4 space-y-3" data-id="element-1531">
                <div className="flex justify-between items-start" data-id="element-1532">
                  <div className="flex items-center gap-2.5" data-id="element-1533">
                    <img src={position.logo} alt={position.symbol} className="w-8 h-8 object-contain" data-id="element-1534" />
                    <div data-id="element-1535">
                      <div className="font-medium text-text-primary text-sm" data-id="element-1536">
                        {position.pair}
                      </div>
                      <div className="flex items-center gap-2 mt-1" data-id="element-1537">
                        <div className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${position.side === 'Long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-1538">
                          {position.side}
                        </div>
                        <div className="px-1.5 py-0.5 bg-brand-purple/10 text-brand-purple-light text-[10px] font-medium rounded" data-id="element-1539">
                          {position.leverage}x
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-1540">
                    <div className="text-xs text-text-secondary mb-0.5" data-id="element-1541">
                      Unrealized P&L
                    </div>
                    <div className={`font-numbers text-sm font-medium ${position.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1542">
                      {position.unrealizedPnl >= 0 ? '+' : ''}$
                      {position.unrealizedPnl.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </div>
                    <div className={`text-xs ${position.unrealizedPnlPercent >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1543">
                      ({position.unrealizedPnlPercent >= 0 ? '+' : ''}
                      {position.unrealizedPnlPercent.toFixed(2)}%)
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1544">
                  <div data-id="element-1545">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1546">Size</div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1547">
                      {position.size.toLocaleString('en-US', {
                    minimumFractionDigits: 4,
                    maximumFractionDigits: 4
                  })}
                    </div>
                    <div className="text-xs text-text-secondary" data-id="element-1548">
                      $
                      {positionValue.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </div>
                  </div>
                  <div data-id="element-1549">
                    <div className="text-xs text-text-secondary mb-1" data-id="element-1550">
                      Entry / Mark
                    </div>
                    <div className="font-numbers text-sm text-text-primary" data-id="element-1551">
                      $
                      {position.entryPrice.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </div>
                    <div className="font-numbers text-xs text-text-secondary" data-id="element-1552">
                      $
                      {position.markPrice.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-3 border-t border-border-subtle" data-id="element-1553">
                  <button className="flex-1 px-3 py-2 text-xs font-medium text-text-secondary hover:text-text-primary bg-background-secondary hover:bg-border-default rounded-lg transition-colors" data-id="element-1554">
                    Limit Close
                  </button>
                  <button className="flex-1 px-3 py-2 text-xs font-medium text-white bg-brand-purple/20 backdrop-blur-sm hover:bg-brand-purple/30 rounded-lg transition-colors border border-transparent" style={{
                borderImage: 'linear-gradient(to bottom, rgba(122, 46, 255, 0.6), rgba(122, 46, 255, 0.15)) 1'
              }} data-id="element-1555">
                    Market Close
                  </button>
                </div>
              </div>;
        })}
        </div>

        {/* Positions Table */}
        <div className="hidden md:block overflow-x-auto" data-id="element-1556">
          <table className="w-full min-w-[800px]" data-id="element-1557">
            <thead className="bg-background-elevated/50" data-id="element-1558">
              <tr className="border-b border-border-subtle" data-id="element-1559">
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1560">
                  Market
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1561">
                  Side
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1562">
                  Size
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1563">
                  Entry Price
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1564">
                  Mark Price
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1565">
                  Leverage
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1566">
                  Unrealized P&L
                </th>
                <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1567">
                  Close by
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1568">
              {futuresPositions.map((position, idx) => {
              const positionValue = position.size * position.markPrice;
              return <tr key={`${position.pair}-${idx}`} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1569">
                    {/* Market */}
                    <td className="px-4 py-3" data-id="element-1570">
                      <div className="flex items-center gap-2.5" data-id="element-1571">
                        <img src={position.logo} alt={position.symbol} className="w-6 h-6 object-contain" data-id="element-1572" />
                        <div data-id="element-1573">
                          <div className="font-medium text-text-primary text-sm" data-id="element-1574">
                            {position.pair}
                          </div>
                          <div className="text-xs text-text-secondary" data-id="element-1575">
                            Perpetual
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Side */}
                    <td className="px-4 py-3" data-id="element-1576">
                      <div className={`inline-flex px-2 py-1 text-xs font-medium rounded ${position.side === 'Long' ? 'bg-success/10 text-success' : 'bg-danger/10 text-danger'}`} data-id="element-1577">
                        {position.side}
                      </div>
                    </td>

                    {/* Size */}
                    <td className="px-4 py-3 text-right" data-id="element-1578">
                      <div className="font-numbers text-sm text-text-primary whitespace-nowrap" data-id="element-1579">
                        {position.size.toLocaleString('en-US', {
                      minimumFractionDigits: 4,
                      maximumFractionDigits: 4
                    })}{' '}
                        <span className="text-text-secondary text-xs" data-id="element-1580">
                          {position.symbol}
                        </span>
                      </div>
                      <div className="text-xs text-text-secondary whitespace-nowrap" data-id="element-1581">
                        $
                        {positionValue.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                    </td>

                    {/* Entry Price */}
                    <td className="px-4 py-3 text-right" data-id="element-1582">
                      <div className="font-numbers text-sm text-text-primary whitespace-nowrap" data-id="element-1583">
                        $
                        {position.entryPrice.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                    </td>

                    {/* Mark Price */}
                    <td className="px-4 py-3 text-right" data-id="element-1584">
                      <div className="font-numbers text-sm text-text-primary whitespace-nowrap" data-id="element-1585">
                        $
                        {position.markPrice.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                    </td>

                    {/* Leverage */}
                    <td className="px-4 py-3 text-right" data-id="element-1586">
                      <div className="px-2 py-1 bg-brand-purple/10 text-brand-purple-light text-xs font-medium rounded inline-block" data-id="element-1587">
                        {position.leverage}x
                      </div>
                    </td>

                    {/* Unrealized P&L */}
                    <td className="px-4 py-3 text-right" data-id="element-1588">
                      <div className={`font-numbers text-sm font-medium whitespace-nowrap ${position.unrealizedPnl >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1589">
                        {position.unrealizedPnl >= 0 ? '+' : ''}$
                        {position.unrealizedPnl.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                      </div>
                      <div className={`text-xs whitespace-nowrap ${position.unrealizedPnlPercent >= 0 ? 'text-success' : 'text-danger'}`} data-id="element-1590">
                        ({position.unrealizedPnlPercent >= 0 ? '+' : ''}
                        {position.unrealizedPnlPercent.toFixed(2)}%)
                      </div>
                    </td>

                    {/* Close by */}
                    <td className="px-4 py-3 text-right" data-id="element-1591">
                      <div className="flex items-center justify-end gap-2" data-id="element-1592">
                        <button className="px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary bg-background-elevated hover:bg-border-default rounded-lg transition-colors whitespace-nowrap" title="Close position with limit order" data-id="element-1593">
                          Limit
                        </button>
                        <button className="px-3 py-1.5 text-xs font-medium text-white bg-brand-purple/20 backdrop-blur-sm hover:bg-brand-purple/30 rounded-lg transition-colors whitespace-nowrap border border-transparent" title="Close position at market price" style={{
                      borderImage: 'linear-gradient(to bottom, rgba(122, 46, 255, 0.6), rgba(122, 46, 255, 0.15)) 1'
                    }} data-id="element-1594">
                          Market
                        </button>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>;
  }
  // Only show staking/earn interface for earn account type
  if (accountType !== 'earn') {
    return <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-1595">
        <div className="flex flex-col items-center justify-center py-16" data-id="element-1596">
          <div className="w-16 h-16 rounded-full bg-background-elevated flex items-center justify-center mb-4" data-id="element-1597">
            <PieChart size={32} className="text-text-tertiary" data-id="element-1598" />
          </div>
          <p className="text-text-secondary text-sm" data-id="element-1599">
            No data available for this account type
          </p>
        </div>
      </div>;
  }
  const currentAssets = activeTab === 'crypto-staking' ? stakingAssets : usdtYieldProducts;
  // Calculate totals
  const totalStaked = currentAssets.reduce((sum, asset) => sum + asset.totalValue, 0);
  const totalEstimatedRewards = currentAssets.reduce((sum, asset) => sum + asset.estimatedRewards * asset.currentPrice, 0);
  return <div className="glass-border rounded-[20px] overflow-hidden" data-id="element-1600">
      {/* Header with Tabs */}
      <div className="border-b border-border-subtle" data-id="element-1601">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-1602">
          <div className="flex items-center gap-1" data-id="element-1603">
            <button onClick={() => setActiveTab('crypto-staking')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'crypto-staking' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1604">
              Crypto Staking
            </button>
            <button onClick={() => setActiveTab('usdt-yield')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'usdt-yield' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1605">
              USDT Yield
            </button>
          </div>
          <div className="flex items-center gap-3" data-id="element-1606">
            <div className="text-right" data-id="element-1607">
              <div className="text-xs text-text-secondary" data-id="element-1608">Total Staked</div>
              <div className="text-sm font-semibold text-text-primary font-numbers" data-id="element-1609">
                $
                {totalStaked.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </div>
            </div>
            <div className="text-right" data-id="element-1610">
              <div className="text-xs text-text-secondary" data-id="element-1611">
                Est. Annual Rewards
              </div>
              <div className="text-sm font-semibold text-success font-numbers" data-id="element-1612">
                +$
                {totalEstimatedRewards.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Cards View */}
      <div className="md:hidden p-4 space-y-4" data-id="element-1613">
        {currentAssets.map((asset, idx) => <div key={`${asset.symbol}-${idx}`} className="bg-background-elevated rounded-xl p-4 space-y-3" data-id="element-1614">
            <div className="flex justify-between items-start" data-id="element-1615">
              <div className="flex items-center gap-2.5" data-id="element-1616">
                <img src={asset.logo} alt={asset.name} className="w-8 h-8 object-contain" data-id="element-1617" />
                <div data-id="element-1618">
                  <div className="font-medium text-text-primary text-sm" data-id="element-1619">
                    {asset.name}
                  </div>
                  <div className="flex items-center gap-2 mt-1" data-id="element-1620">
                    <span className="text-xs text-text-secondary" data-id="element-1621">
                      {asset.symbol}
                    </span>
                    {activeTab === 'crypto-staking' ? <span className="text-xs text-text-tertiary" data-id="element-1622">
                        • {asset.platform}
                      </span> : <div className="flex items-center gap-1 text-xs text-text-tertiary" data-id="element-1623">
                        <span data-id="element-1624">•</span>
                        {asset.lockupPeriod === 'Flexible' ? <Unlock size={10} className="text-success" data-id="element-1625" /> : <Lock size={10} className="text-warning" data-id="element-1626" />}
                        <span data-id="element-1627">{(asset as any).product}</span>
                      </div>}
                  </div>
                </div>
              </div>
              <div className="text-right" data-id="element-1628">
                <div className="text-xs text-text-secondary mb-0.5" data-id="element-1629">
                  Total Value
                </div>
                <div className="font-numbers text-sm text-text-primary font-medium" data-id="element-1630">
                  $
                  {asset.totalValue.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-border-subtle" data-id="element-1631">
              <div data-id="element-1632">
                <div className="text-xs text-text-secondary mb-1" data-id="element-1633">
                  Staked Amount
                </div>
                <div className="font-numbers text-sm text-text-primary" data-id="element-1634">
                  {asset.stakedAmount.toLocaleString('en-US', {
                minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 4,
                maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 4
              })}
                </div>
              </div>
              <div data-id="element-1635">
                <div className="text-xs text-text-secondary mb-1" data-id="element-1636">APY</div>
                <div className="font-numbers text-sm text-success font-medium" data-id="element-1637">
                  {asset.apy.toFixed(1)}%
                </div>
              </div>
              <div data-id="element-1638">
                <div className="text-xs text-text-secondary mb-1" data-id="element-1639">Lockup</div>
                <div className="flex items-center gap-1.5" data-id="element-1640">
                  {asset.lockupPeriod === 'Flexible' ? <>
                      <Unlock size={12} className="text-success" data-id="element-1641" />
                      <span className="text-sm text-success" data-id="element-1642">Flexible</span>
                    </> : <>
                      <Lock size={12} className="text-warning" data-id="element-1643" />
                      <span className="text-sm text-text-primary" data-id="element-1644">
                        {asset.lockupPeriod}
                      </span>
                    </>}
                </div>
              </div>
              <div data-id="element-1645">
                <div className="text-xs text-text-secondary mb-1" data-id="element-1646">
                  Est. Annual Rewards
                </div>
                <div className="font-numbers text-sm text-success font-medium" data-id="element-1647">
                  +
                  {asset.estimatedRewards.toLocaleString('en-US', {
                minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 2,
                maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 2
              })}{' '}
                  {asset.symbol}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-3 border-t border-border-subtle" data-id="element-1648">
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-brand-purple/20 backdrop-blur-sm hover:bg-brand-purple/30 rounded-xl transition-colors border border-[rgba(122,46,255,0.25)] border-t-[rgba(122,46,255,0.5)] hover:border-[rgba(122,46,255,0.35)] hover:border-t-[rgba(122,46,255,0.6)]" data-id="element-1649">
                <Plus size={14} data-id="element-1650" />
                Add More
              </button>
              <button className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-text-primary bg-background-secondary hover:bg-border-default rounded-lg transition-colors" data-id="element-1651">
                <Minus size={14} data-id="element-1652" />
                Unstake
              </button>
            </div>
          </div>)}
      </div>

      {/* Staking Table */}
      <div className="hidden md:block overflow-x-auto" data-id="element-1653">
        <table className="w-full min-w-[800px]" data-id="element-1654">
          <thead className="bg-background-elevated/50" data-id="element-1655">
            <tr className="border-b border-border-subtle" data-id="element-1656">
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1657">
                Asset
              </th>
              {activeTab === 'crypto-staking' && <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1658">
                  Platform
                </th>}
              {activeTab === 'usdt-yield' && <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1659">
                  Product
                </th>}
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1660">
                Staked Amount
              </th>
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1661">
                APY
              </th>
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1662">
                Lockup
              </th>
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1663">
                Est. Annual Rewards
              </th>
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1664">
                Total Value
              </th>
              <th className="px-4 py-2.5 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1665">
                Actions
              </th>
            </tr>
          </thead>
          <tbody data-id="element-1666">
            {currentAssets.map((asset, idx) => <tr key={`${asset.symbol}-${idx}`} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1667">
                <td className="px-4 py-3" data-id="element-1668">
                  <div className="flex items-center gap-2.5" data-id="element-1669">
                    <img src={asset.logo} alt={asset.name} className="w-5 h-5 object-contain" data-id="element-1670" />
                    <div data-id="element-1671">
                      <div className="font-medium text-text-primary text-sm" data-id="element-1672">
                        {asset.name}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-1673">
                        {asset.symbol}
                      </div>
                    </div>
                  </div>
                </td>
                {activeTab === 'crypto-staking' && <td className="px-4 py-3" data-id="element-1674">
                    <div className="text-sm text-text-primary" data-id="element-1675">
                      {asset.platform}
                    </div>
                  </td>}
                {activeTab === 'usdt-yield' && <td className="px-4 py-3" data-id="element-1676">
                    <div className="flex items-center gap-2" data-id="element-1677">
                      {asset.lockupPeriod === 'Flexible' ? <Unlock size={14} className="text-success" data-id="element-1678" /> : <Lock size={14} className="text-warning" data-id="element-1679" />}
                      <span className="text-sm text-text-primary" data-id="element-1680">
                        {(asset as any).product}
                      </span>
                    </div>
                  </td>}
                <td className="px-4 py-3 text-right" data-id="element-1681">
                  <div className="font-numbers text-sm text-text-primary whitespace-nowrap" data-id="element-1682">
                    {asset.stakedAmount.toLocaleString('en-US', {
                  minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 4,
                  maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 4
                })}{' '}
                    <span className="text-text-secondary" data-id="element-1683">{asset.symbol}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1684">
                  <div className="font-numbers text-sm text-success font-medium" data-id="element-1685">
                    {asset.apy.toFixed(1)}%
                  </div>
                  <div className="text-xs text-text-secondary" data-id="element-1686">
                    {asset.rewardsFrequency}
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1687">
                  <div className="flex items-center justify-end gap-1.5" data-id="element-1688">
                    {asset.lockupPeriod === 'Flexible' ? <>
                        <Unlock size={12} className="text-success" data-id="element-1689" />
                        <span className="text-sm text-success whitespace-nowrap" data-id="element-1690">
                          Flexible
                        </span>
                      </> : <>
                        <Lock size={12} className="text-warning" data-id="element-1691" />
                        <span className="text-sm text-text-primary whitespace-nowrap" data-id="element-1692">
                          {asset.lockupPeriod}
                        </span>
                      </>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1693">
                  <div className="font-numbers text-sm text-success font-medium whitespace-nowrap" data-id="element-1694">
                    +
                    {asset.estimatedRewards.toLocaleString('en-US', {
                  minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 2,
                  maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 2
                })}{' '}
                    {asset.symbol}
                  </div>
                  <div className="text-xs text-text-secondary whitespace-nowrap" data-id="element-1695">
                    $
                    {(asset.estimatedRewards * asset.currentPrice).toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1696">
                  <div className="font-numbers text-sm text-text-primary font-medium whitespace-nowrap" data-id="element-1697">
                    $
                    {asset.totalValue.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}
                  </div>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-1698">
                  <div className="flex items-center justify-end gap-2" data-id="element-1699">
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-brand-purple/20 backdrop-blur-sm hover:bg-brand-purple/30 rounded-xl transition-colors whitespace-nowrap border border-[rgba(122,46,255,0.25)] border-t-[rgba(122,46,255,0.5)] hover:border-[rgba(122,46,255,0.35)] hover:border-t-[rgba(122,46,255,0.6)]" title="Add more to stake" data-id="element-1700">
                      <Plus size={12} data-id="element-1701" />
                      Add More
                    </button>
                    <button className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-primary bg-background-elevated hover:bg-border-default rounded-lg transition-colors whitespace-nowrap" title="Unstake funds" data-id="element-1702">
                      <Minus size={12} data-id="element-1703" />
                      Unstake
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}